
// Google Analytics és Tag Manager segédszolgáltatás
// Ez a szolgáltatás kezeli az események küldését a dataLayer-en keresztül

// Global típus deklaráció a dataLayer számára
declare global {
  interface Window {
    dataLayer?: any[];
  }
}

// Ellenőrizzük, hogy a dataLayer létezik-e
const initializeDataLayer = () => {
  if (typeof window !== 'undefined') {
    if (!window.dataLayer) {
      window.dataLayer = [];
    }
  }
};

/**
 * Egyszerű esemény küldése a Google Tag Manager dataLayer-jébe
 * @param eventName Az esemény neve
 * @param eventParams Az esemény további paraméterei
 */
export const sendEvent = (eventName: string, eventParams?: Record<string, any>) => {
  // Inicializáljuk a dataLayer-t, ha még nem létezik
  initializeDataLayer();

  // Ellenőrizzük, hogy production környezetben vagyunk-e
  if (import.meta.env.PROD) {
    // Esemény küldése a dataLayer-be
    window.dataLayer?.push({
      event: eventName,
      ...eventParams
    });
    console.log(`Analytics event sent: ${eventName}`, eventParams);
  } else {
    // Fejlesztői környezetben csak logoljuk az eseményt
    console.log(`[DEV] Analytics event would be sent: ${eventName}`, eventParams);
  }
};

/**
 * Oldal megtekintés esemény küldése
 * @param pageTitle Az oldal címe
 * @param pagePath Az oldal útvonala (default: current path)
 */
export const sendPageView = (pageTitle: string, pagePath?: string) => {
  const path = pagePath || window.location.pathname;
  
  sendEvent('page_view', {
    page_title: pageTitle,
    page_path: path,
    page_location: window.location.href
  });
};

/**
 * Felhasználói bejelentkezés esemény küldése
 * @param method A bejelentkezés módja (email, social, stb.)
 */
export const sendLoginEvent = (method: string = 'email') => {
  sendEvent('login', { method });
};

/**
 * Felhasználói regisztráció esemény küldése
 * @param method A regisztráció módja
 * @param userRole A felhasználó szerepköre
 */
export const sendSignUpEvent = (method: string = 'email', userRole?: string) => {
  sendEvent('sign_up', { 
    method,
    user_role: userRole
  });
};

/**
 * Esemény megtekintés küldése
 * @param event Esemény adatok
 */
export const sendEventView = (event: any) => {
  if (!event) return;
  
  sendEvent('view_item', {
    event_id: event.id || event.eventId,
    event_name: event.title || event.name,
    event_type: event.type,
    event_category: event.category,
    event_date: event.startDate || event.dateStart,
    event_location: event.location || (event.venue ? (event.venue.online ? 'Online' : event.venue.city) : 'N/A')
  });
};

/**
 * Részletes esemény megtekintés küldése
 * @param event Esemény adatok
 */
export const sendEventDetailView = (event: any) => {
  if (!event) return;
  
  console.log('Sending event detail view to GA:', event);
  
  sendEvent('view_item_details', {
    event_id: event.id || event.eventId,
    event_name: event.title || event.name,
    event_type: event.type,
    event_category: event.category,
    event_date: event.startDate || event.dateStart,
    event_location: event.location || (event.venue ? (event.venue.online ? 'Online' : event.venue.city) : 'N/A')
  });
};

/**
 * Esemény létrehozás küldése
 * @param eventType Esemény típusa
 * @param eventCategory Esemény kategóriája
 */
export const sendEventCreate = (eventType: string, eventCategory: string) => {
  sendEvent('create_event', {
    event_type: eventType,
    event_category: eventCategory
  });
};

/**
 * Keresési esemény küldése
 * @param searchTerm Keresett kifejezés
 * @param resultsCount Találatok száma
 */
export const sendSearchEvent = (searchTerm: string, resultsCount: number) => {
  sendEvent('search', {
    search_term: searchTerm,
    results_count: resultsCount
  });
};

/**
 * Szűrési esemény küldése
 * @param filterType Szűrő típusa
 * @param filterValue Szűrő értéke
 */
export const sendFilterEvent = (filterType: string, filterValue: string | string[]) => {
  sendEvent('filter_apply', {
    filter_type: filterType,
    filter_value: Array.isArray(filterValue) ? filterValue.join(',') : filterValue
  });
};

/**
 * Nézet váltás esemény küldése
 * @param viewType Nézet típusa (calendar, list, stb.)
 */
export const sendViewChangeEvent = (viewType: string) => {
  sendEvent('view_change', {
    view_type: viewType
  });
};
